import isUndefined from "lodash/isUndefined";

import Backbone from "pages/common/lib/backbone-helpers";
import CONSTANTS from "const/constants";
import { deepExtend } from "util/object-utils";

const FeaturesConfigModel = Backbone.Model.extend({
    defaults: {
        organizationFeaturesConfig: {},
        contextFeaturesConfig: {},
        featuresConfig: {}
    },

    initialize: function () {
        this.on(
            `change:organizationFeaturesConfig change:contextFeaturesConfig`,
            this.onUpdate
        );
    },

    onUpdate: function () {
        this.set(
            `featuresConfig`,
            deepExtend(
                {},
                this.get(`organizationFeaturesConfig`),
                this.get(`contextFeaturesConfig`)
            )
        );
    },

    reset: function (properties) {
        this.clear();
        this.set(properties);
    },

    isCreativeSetEnabled: function () {
        return !(
            this.has(`featuresConfig`) &&
            this.get(`featuresConfig`).studioFeature &&
            this.get(`featuresConfig`).studioFeature.creativeSetsEnabled ===
                `false`
        );
    },

    isCreativeSetAdvancedOptionsEnabled: function () {
        const creativeSetConfig = this.getStudioFeature(`creativeSetConfig`);
        return !(
            isUndefined(creativeSetConfig) &&
            creativeSetConfig.displayAdvancedOptionsJson === `false`
        );
    },

    hasStudioFeature: function () {
        return (
            this.has(`featuresConfig`) &&
            this.get(`featuresConfig`).studioFeature
        );
    },

    getStudioFeature: function (key) {
        if (!this.hasStudioFeature()) {
            return undefined;
        }

        const val = this.get(`featuresConfig`).studioFeature[key];
        if (val === `true`) {
            return true;
        } else if (val === `false`) {
            return false;
        } else {
            return val;
        }
    },

    getFeatureByType: function (featureType) {
        return this.get(`featuresConfig`)
            ? this.get(`featuresConfig`)[featureType]
            : {};
    },

    isFlexarEnabled: function () {
        return this.getStudioFeature(`flexarEnabled`) === true;
    },

    isFlexarVisible: function () {
        const flexarVisible = this.getStudioFeature(`flexarVisible`);
        return flexarVisible === true || isUndefined(flexarVisible);
    },

    isOverlayEnabled: function () {
        return this.getStudioFeature(`overlayEnabled`) === true;
    },

    isOverlayVisible: function () {
        const overlayVisible = this.getStudioFeature(`overlayVisible`);
        return overlayVisible === true || isUndefined(overlayVisible);
    },

    getDefaultAspectRatio: function () {
        return this.getStudioFeature(`defaultAspectRatio`) || `16:9`;
    },

    hasDefaultAspectRatio: function () {
        return !isUndefined(this.getStudioFeature(`defaultAspectRatio`));
    },

    isClipDeleteEnabled: function () {
        let response = true;
        if (!App.Models.user.get(`isAdmin`)) {
            if (
                !isUndefined(this.getStudioFeature(`clipDeleteEnabled`)) &&
                this.getStudioFeature(`clipDeleteEnabled`) === false
            ) {
                response = false;
            }
        }
        return response;
    },

    isTwitterNativeEnabled: function () {
        return (
            !isUndefined(this.getFeatureByType(`twitterFeature`)) &&
            this.getFeatureByType(`twitterFeature`).nativeVideosEnabled ===
                `true`
        );
    },

    isComcastHasBugToggleEnabled: function () {
        const { hasBugToggleEnabled } = this.get(`thePlatformFeature`);
        return hasBugToggleEnabled === "true";
    },

    isGifDisabled: function () {
        return this.getStudioFeature(`gifDisabled`);
    },

    is360VideoEnabled: function () {
        return this.getStudioFeature(`video360Enabled`);
    },

    isFacebookDonateCharityEnabled: function () {
        return this.getFeatureByType(`facebookFeature`).donateCharityEnabled;
    },

    isFacebookAdBreakEnabled: function () {
        const facebookFeature = this.getFeatureByType(`facebookFeature`);
        return (
            !isUndefined(facebookFeature) &&
            facebookFeature.adBreakEnabled === `true`
        );
    },

    getDefaultSaveVodToCampaign: function () {
        return this.getStudioFeature(`defaultSaveVodToCampaign`);
    },
    getSupportedMagnifiSports: function () {
        const sportMap =
            this.getStudioFeature(`magnifiFeature`)?.supportedSports;

        const supportedSports = Object.entries(sportMap)
            .filter(([sport, isEnabled]) => isEnabled === "true")
            .map(([sport]) => sport);

        return supportedSports;
    },

    isWebSourceDisabled: function () {
        return this.getStudioFeature(`webSourceDisabled`);
    },

    isMfaEnabled: function () {
        const mfaEnabledFeature = this.getStudioFeature(`mfaEnabled`);
        return !(!isUndefined(mfaEnabledFeature) && !mfaEnabledFeature);
    },

    isEmbedMediaEnabled: function () {
        return this.getStudioFeature(`embedMediaEnabled`) === true;
    },

    getMaxBroadcastDestinations: function () {
        return this.getStudioFeature(
            `maxConcurrentBroadcastDestinationsPerStream`
        );
    },

    getProducerMaxLayers: function () {
        return (
            this.getStudioFeature(`producerMaxLayers`) ||
            CONSTANTS.PRODUCER_MAX_LAYERS
        );
    },

    isCustomRtmpCredentialsEnabled: function () {
        return this.getStudioFeature(`customRtmpCredentialsEnabled`);
    },

    isTrimmerThumbnailsEnabled: function () {
        return (
            this.getStudioFeature(`trimmerThumbnailEnabled`) !== true &&
            window.createImageBitmap
        );
    },

    isTranscriptionEnabled: function () {
        return this.getStudioFeature(`editorTranscriptionEnabled`) === true;
    },

    isProducerAutoRecueEnabled: function () {
        return this.getStudioFeature(`unleashProducerAutoRecue`) === true;
    },

    isProducerCompressionEnabled: function () {
        return this.getStudioFeature(`unleashProducerCompression`) === true;
    },

    isProducerDynamicTransitionsEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerDynamicTransitions`) === true
        );
    },

    isProducerEditLiveSourceEnabled: function () {
        return this.getStudioFeature(`unleashProducerEditLiveSource`) === true;
    },

    isProducerMediaPlayerTrimmerEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerMediaPlayerTrimmer`) === true
        );
    },

    isProducerStreamSyncEnabled: function () {
        return this.getStudioFeature(`producerStreamSyncEnabled`) === true;
    },

    isIntercomEnabled: function () {
        return this.getStudioFeature(`intercom-enabled`) === true;
    },

    isCrosspostingEnabled: function () {
        return this.getStudioFeature(`facebook-crossposting`) === true;
    },

    isNewHelpIconEnabled: function () {
        return this.getStudioFeature(`unleashNewHelpIcon`) === true;
    },

    isEpgEnabled: function () {
        return this.getStudioFeature(`epgEnabled`) === true;
    },

    isEditorEnabled: function () {
        return this.getStudioFeature(`editorEnabled`) === true;
    },

    isShareHistoryV2Enabled: function () {
        return false;
    },

    isFeatureFlagAudioMixerEnabled: function () {
        return this.getStudioFeature(`producerAudioMixerDisabled`) !== true;
    },

    isAutomationEnabled: function () {
        return this.getStudioFeature(`automationEnabled`) === true;
    },

    isCloudProducerEnabled: function () {
        return this.getStudioFeature(`cloudProducerEnabled`) === true;
    },

    isServiceWorkerEnabled: function () {
        return this.getStudioFeature(`unleashServiceWorker`) === true;
    },

    isHeaderRevampEnabled: function () {
        return this.getStudioFeature(`unleashHeaderRevamp`) === true;
    },

    isLiveSourceAudioStreamsEnabled: function () {
        return this.getStudioFeature(`unleashLiveSourceAudioStreams`) === true;
    },

    isLiveClippingExportDurationMetricEnabled: function () {
        return (
            this.getStudioFeature(`usageSummaryConfig`)
                ?.disableLiveClippingExportDurationMetric !== `true`
        );
    },

    isMultiviewerQualitySettingEnabled: function () {
        return (
            this.getStudioFeature(`unleashMultiviewerQualitySetting`) === true
        );
    },

    isProducerGuestRemoteControlsEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerGuestRemoteControls`) === true
        );
    },

    isProducerQualitySettingEnabled: function () {
        return this.getStudioFeature(`unleashProducerQualitySetting`) === true;
    },

    isProducerMultiDisplaySettingsEnabled: function () {
        return (
            this.getStudioFeature(
                `unleashProducerMultiDisplaySettingsEnabled`
            ) === true
        );
    },

    isProducerMagicLinksEnabled: function () {
        return this.getStudioFeature(`unleashProducerMagicLinks`) === true;
    },

    isProducerStereoBalanceEnabled: function () {
        return this.getStudioFeature(`unleashProducerStereoBalance`) === true;
    },

    isProducerSwitchToPreviewEnabled: function () {
        return this.getStudioFeature(`unleashProducerSwitchToPreview`) === true;
    },

    isProducerSupportedBrowsersOnlyEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerSupportedBrowsersOnly`) ===
            true
        );
    },

    isSocialCredentialsMigrationEnabled: function () {
        return (
            this.getStudioFeature(`unleashSocialCredentialsMigration`) === true
        );
    },
    isProducerVideoAudioDecodeEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerVideoAudioDecode`) === true
        );
    },

    isEditorFifthLayerEnabled: function () {
        return this.getStudioFeature(`unleashEditorFifthLayer`) === true;
    },
    isHomeSchedulerToDvrEnabled: function () {
        return this.getStudioFeature(`unleashHomeSchedulerToDVR`) === true;
    },
    isProducerMultiTrackEnabled: function () {
        return this.getStudioFeature(`unleashProducerMultiTrack`) === true;
    },
    isKeyBindingsActiveTabsIndicatorEnabled: function () {
        return (
            this.getStudioFeature(`unleashKeyBindingsActiveTabsIndicator`) ===
            true
        );
    },
    isKeyboardLocalizationEnabled: function () {
        return this.getStudioFeature(`unleashKeyboardLocalization`) === true;
    },
    isGloboCredentialEnabled: function () {
        return this.getStudioFeature(`globoCredentialEnabled`) === true;
    },
    isHideUdvrBroadcastsEnabled: function () {
        return this.getStudioFeature(`unleashHideUdvrBroadcasts`);
    },
    isUdvrBroadcastsEnabled: function () {
        return this.getStudioFeature(`unleashAllowUDVRBroadcasting`);
    },
    isProducerTargetSourceByTitleEnabled: function () {
        return this.getStudioFeature(`unleashProducerTargetSourceByTitle`);
    },
    isProducerUDVRMarkersEnabled: function () {
        return this.getStudioFeature(`unleashProducerUDVRMarkers`);
    },
    isGrabyoMobileAppEnabled: function () {
        const mobileFeature = this.getFeatureByType(`mobileFeature`);
        if (mobileFeature) {
            return mobileFeature.mobileAccessEnabled === "true";
        }
        return false;
    },
    isMagnifiEnabled: function () {
        if (
            App.Models.user.get("roles").includes(CONSTANTS.USER_ROLES.ADMIN) ||
            App.Models.user.get("roles").includes(CONSTANTS.USER_ROLES.MANAGER)
        ) {
            return this.getStudioFeature(`magnifiFeature`)?.enabled === "true";
        }
        return false;
    },
    isRepeatingXKeysShuttleEnabled: function () {
        return this.getStudioFeature(`unleashRepeatingXKeysShuttle`);
    },
    isOutputMetricsEnabled: function () {
        return this.getStudioFeature(`unleashOutputMetrics`);
    },

    isTwitterMonetisationPresetsEnabled: function () {
        return this.getStudioFeature(`unleashTwitterMonetisationPresets`);
    },
    isThreadsCredentialEnabled: function () {
        return this.getStudioFeature(`unleashThreadsCredential`);
    },
    isStudioShareSidebarMigrationEnabled: function () {
        return this.getStudioFeature(`unleashStudioShareSidebarMigration`);
    },
    isProducerGatewayDownloadEnabled: function () {
        return this.getStudioFeature(`unleashProducerGatewayDownload`);
    },
    isMagnifiGeniusDataFeedEnabled: function () {
        return this.getStudioFeature(`magnifi-genius-datafeed`);
    },
    isProIngestV2MetricsEnabled: function () {
        return this.getStudioFeature(`unleashProIngestV2Metrics`);
    },
    isGenAiTitleDescVisible: function () {
        return this.getStudioFeature(`unleashGenAiTitleDesc`);
    },
    isGenAiTitleDescEnabled: function () {
        return this.getStudioFeature(`unleashGenAiTitleDescEnableFeature`);
    },
    isMagnifiHighlightsEnabled: function () {
        return this.getStudioFeature(`unleashMagnifiHighlights`);
    },
    isTrimmerPlaybackSyncMLCEnabled: function () {
        return this.getStudioFeature(`unleashTrimmerPlaybackSyncMLC`);
    },
    isProducerTextOverlayEnabled: function () {
        return this.getStudioFeature(`unleashProducerTextOverlay`) === true;
    },
    isProducerOpenCaptionsEnabled: function () {
        return this.getStudioFeature(`producerOpenCaptions`) === true;
    },
    isProducerThreadsWebsourceEnabled: function () {
        return (
            this.getStudioFeature(`unleashProducerThreadsWebsource`) === true
        );
    },
    isMrssIngestEnabled: function () {
        return this.getStudioFeature(`mrssIngestEnabled`);
    },
    isBulkStartStopEnabled: function () {
        return this.getStudioFeature(`unleashStudioBulkStartStop`);
    },
    isYoutubePublishTemplateEnabled: function () {
        return this.getStudioFeature(`unleashYoutubePublishTemplate`);
    },
    isStreamSchedulerNewCalendarEnabled: function () {
        return this.getStudioFeature(`unleashStudioNewStreamSchedulerCalendar`);
    }
});

export default FeaturesConfigModel;
